import { useTranslation } from "react-i18next";
import { Button } from "../../../ui/Button";
import type { Widget } from "../../../widgets/types";
import type { WidgetPage } from "../page/queries";
import { ActionStatus } from "./ActionStatus";
import { OPEN } from "./resolve";
import type { DeviceActions } from "./useDeviceActions";

/** The card's `quick_action` as a widget-like target, so ActionStatus can follow it; undefined without one. */
export function quickAction(device: string, page: WidgetPage): Widget | undefined {
  const key = (page.card as { quick_action?: unknown } | undefined)?.quick_action;
  if (typeof key !== "string" || page.actions[key]?.kind !== "command") return undefined;
  return {
    id: `quick-${key}`,
    type: "quick_action",
    position: { x: 0, y: 0, w: 3, h: 1 },
    bindings: [{ device, command: key }],
  };
}

/**
 * The card's quick action (WEB-CMD-01): one button sending the profile's command, on the device page's hero and the
 * device sheet. Guarded commands confirm first, like any control.
 */
export function QuickAction({
  device,
  page,
  actions,
  now,
}: {
  device: string;
  page: WidgetPage;
  actions: DeviceActions;
  now: Date;
}) {
  const { t } = useTranslation();
  const target = quickAction(device, page);
  const key = target?.bindings?.[0]?.command;
  const info = key ? page.actions[key] : undefined;
  if (!target || !key || !info) return null;
  const k = `${device}/${key}`;
  const c = actions.commands[k];
  const waiting = k in actions.sending || (!!c && OPEN.has(c.state));
  const ready = info.allowed && !!actions.docFor(device);
  return (
    <div className="flex flex-col gap-1">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          disabled={!ready || waiting}
          onClick={() => actions.onAction({ kind: "command", device, key, value: true })}
        >
          {info.name}
        </Button>
        {waiting && <span className="text-sm text-gray-800">{t("widget:state.waiting")}</span>}
        {!info.allowed && <span className="text-sm text-gray-700">{t("widget:state.noPermission")}</span>}
      </div>
      <ActionStatus widget={target} actions={actions} now={now} />
    </div>
  );
}
